import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import type {
  TFACodeRequestEvent,
  TFACodeExpiredEvent,
  TFACodeCancelledEvent,
} from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import TFACodeInput from '../chat/TFACodeInput';
import api from '../../api';

/**
 * App-level bridge that surfaces the 2FA request modal from any route.
 *
 * Previously this logic lived inside ChatInterface, which meant TFA
 * requests only auto-opened when the user happened to be on a conversation
 * page. Elsewhere they had to click the web push notification to navigate
 * in. Mounted next to CreditsPusherListener under PusherProvider so the
 * userChannel Pusher events are received regardless of route, and the
 * modal pops on top of whatever the user is looking at.
 *
 * Also drains the ?tfa_request_id=... URL param as a fallback for when
 * the app opens via a push-notification click — handy when the Pusher
 * event was missed (e.g. app was fully closed).
 */
const TFAPusherListener: React.FC = () => {
  const pusherContext = usePusher(true);
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTFARequest, setActiveTFARequest] =
    useState<TFACodeRequestEvent | null>(null);

  const userChannel = pusherContext?.userChannel;

  // Live Pusher listener — fires when the agent calls request2FAFromUser
  // while the app is already open.
  useEffect(() => {
    if (!userChannel) return;

    const handleTFARequest = (data: TFACodeRequestEvent) => {
      console.log('[TFA] Code request received:', data);
      if (Date.now() < data.expiresAt) {
        setActiveTFARequest(data);
      }
    };

    const handleTFAExpired = (data: TFACodeExpiredEvent) => {
      console.log('[TFA] Request expired:', data);
      setActiveTFARequest((prev) => {
        if (prev?.requestId === data.requestId) {
          return { ...prev, expiresAt: Date.now() - 1 };
        }
        return prev;
      });
    };

    // Fired when an MFA request is invalidated before the user responds —
    // e.g. the operator clicked Cancel in the browser viewer, or the email
    // auto-extraction path delivered the code. Dismiss the modal silently.
    const handleTFACancelled = (data: TFACodeCancelledEvent) => {
      console.log('[TFA] Request cancelled:', data);
      setActiveTFARequest((prev) => {
        if (prev?.requestId === data.requestId) return null;
        return prev;
      });
    };

    userChannel.bind('tfa-code-request', handleTFARequest);
    userChannel.bind('tfa-code-expired', handleTFAExpired);
    userChannel.bind('tfa-code-cancelled', handleTFACancelled);

    return () => {
      userChannel.unbind('tfa-code-request', handleTFARequest);
      userChannel.unbind('tfa-code-expired', handleTFAExpired);
      userChannel.unbind('tfa-code-cancelled', handleTFACancelled);
    };
  }, [userChannel]);

  // Fallback path — consume ?tfa_request_id=... on navigation. This fires
  // when the user clicks a web push notification and the browser opens the
  // PWA at /conversations?tfa_request_id=<id>; we may have missed the live
  // Pusher event because the app wasn't running.
  const handledRequestIdRef = useRef<string | null>(null);
  useEffect(() => {
    const tfaRequestId = searchParams.get('tfa_request_id');
    if (!tfaRequestId) return;
    if (handledRequestIdRef.current === tfaRequestId) return;
    handledRequestIdRef.current = tfaRequestId;

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get(
          `/browser-agent/tfa-request/${tfaRequestId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (data.status === 'pending' && Date.now() < data.expiresAt) {
          setActiveTFARequest(data as TFACodeRequestEvent);
        }
      } catch (err) {
        console.warn(
          '[TFA] Failed to fetch TFA request from notification:',
          err,
        );
      } finally {
        // Strip the query param so a back-nav / refresh doesn't retrigger.
        // `from_notification` is left alone — it's used by non-TFA push
        // notifications (e.g. plan feedback) and is consumed elsewhere.
        const next = new URLSearchParams(searchParams);
        next.delete('tfa_request_id');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [searchParams, setSearchParams, getAccessTokenSilently]);

  return (
    <TFACodeInput
      request={activeTFARequest}
      onDismiss={() => setActiveTFARequest(null)}
    />
  );
};

export default TFAPusherListener;
